<template>
  <!-- eslint-disable max-len  -->
  <div>
    <PanelHeader
      :titleText="$t(LABEL_TWO_FACTOR_AUTHENTICATION)"
      :helpText="$t(LABEL_OPEN_YOUR_TIME_BASED_ONE_TIME_PASSWORD)"
      :helpTextLeft="true"
    ></PanelHeader>
    <div class="form-group">
      <input
        class="form-control"
        ref="codeField"
        v-model="code"
        :placeholder="$t('Password.Code')"
        v-bind:class="{ 'is-danger': isCodeInvalid }"
        autocapitalize="none"
        autocorrect="off"
        type="text"
        v-on:keydown.enter.prevent="validate"
      />
      <div class="help" v-bind:class="{ 'is-danger': isCodeInvalid }" v-html="codeLabel"></div>
    </div>
    <div class="btn-panel">
      <a
        class="btn btn-full"
        tabindex="0"
        v-bind:class="{ 'is-disabled': processing, 'is-loading': processing }"
        @keyup.enter="validate"
        @click="validate"
        >{{ $t(LABEL_CONTINUE) }}</a
      >
    </div>

    <div style="margin-top: 1.5rem">
      <a @click="displayBackupCode" @keyup.enter="displayBackupCode" style="cursor: pointer"> {{ $t(LABEL_ENTER_ONE_OF_YOUR_BACKUP_CODES) }}</a>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watchEffect, inject, PropType } from 'vue';
import PanelHeader from '@/components/panel-header.vue';
import focus from '@/libs/focus';
import { ToastPluginApi } from 'vue-toast-notification';
import { useI18n } from 'vue-i18n';
import { PasswordResetErrorCode } from '@/password/passwordState';
import {
  LABEL_CONTINUE,
  LABEL_TWO_FACTOR_AUTHENTICATION,
  LABEL_OPEN_YOUR_TIME_BASED_ONE_TIME_PASSWORD,
  LABEL_ENTER_ONE_OF_YOUR_BACKUP_CODES,
} from '@/libs/i18nLabels';

export default defineComponent({
  components: {
    PanelHeader,
  },

  data() {
    return {
      code: '',
    };
  },

  props: {
    errorCode: Object as PropType<PasswordResetErrorCode>,
    processing: Boolean,
  },

  emits: ['codeSubmitted', 'backupCodeSubmitted'],

  setup(props) {
    const toast: ToastPluginApi | undefined = inject('$toast');
    const i18n = useI18n();

    watchEffect(() => {
      if (props.errorCode === PasswordResetErrorCode.TOTPInvalidBackupCode) {
        toast?.open({ message: i18n.t('Password.TOTP_invalid_code'), type: 'error', duration: 5000, position: 'top' });
      }
    });

    return { LABEL_CONTINUE, LABEL_TWO_FACTOR_AUTHENTICATION, LABEL_OPEN_YOUR_TIME_BASED_ONE_TIME_PASSWORD, LABEL_ENTER_ONE_OF_YOUR_BACKUP_CODES };
  },

  methods: {
    validate() {
      if (this.code.length === 0) {
        this.focusCode();
        return;
      }

      this.$emit('codeSubmitted', this.code);
    },

    focusCode() {
      focus(this.$refs.codeField);
    },

    displayBackupCode() {
      // eslint-disable-next-line no-alert
      const backupCode = window.prompt(this.$t('Password.Enter_one_of_your_backup_codes'));
      if (!backupCode || backupCode.trim().length === 0) {
        return;
      }
      this.code = '';
      this.$emit('backupCodeSubmitted', backupCode);
    },
  },

  computed: {
    isCodeInvalid() {
      return this.errorCode === PasswordResetErrorCode.TOTPInvalidCode;
    },

    codeLabel() {
      if (this.isCodeInvalid) {
        this.focusCode();
        return this.$t('Password.TOTP_invalid_code');
      }

      return undefined;
    },
  },

  mounted() {
    this.focusCode();
  },
});
</script>
